import api from '@/services/api';
import { normalizeAmountString } from '@shared/components/form/config-value';
import type {
    LeftToPay,
    LeftToPayReason,
    PaymentLinkInput,
    PaymentLinkPayload,
    PaymentLinkSummary,
    SentPaymentLink,
} from '../types/paymentLinks.types';

const TWO_DP_PATTERN = /^\d+\.\d{2}$/;
const KNOWN_REASONS: readonly LeftToPayReason[] = ['no_elemis_agreement', 'elemis_unavailable', 'paid_in_full'];

const UNAVAILABLE: LeftToPay = { eligible: false, reason: 'elemis_unavailable', leftToPay: null };

const isTwoDp = (value: unknown): value is string => typeof value === 'string' && TWO_DP_PATTERN.test(value);

const isKnownReason = (value: unknown): value is LeftToPayReason =>
    KNOWN_REASONS.includes(value as LeftToPayReason);

/** A malformed body reads as elemis_unavailable rather than throwing. */
export function readLeftToPay(raw: unknown): LeftToPay {
    if (typeof raw !== 'object' || raw === null) return UNAVAILABLE;
    const body = raw as Record<string, unknown>;

    if (body.eligible === true) {
        return isTwoDp(body.left_to_pay) ? { eligible: true, leftToPay: body.left_to_pay } : UNAVAILABLE;
    }
    if (body.eligible !== false || !isKnownReason(body.reason)) return UNAVAILABLE;

    const hasFigure = body.left_to_pay !== undefined && body.left_to_pay !== null;
    if (hasFigure && !isTwoDp(body.left_to_pay)) return UNAVAILABLE;

    const leftToPay = isTwoDp(body.left_to_pay) ? body.left_to_pay : null;
    if (body.reason === 'paid_in_full' && leftToPay === null) return UNAVAILABLE;

    return { eligible: false, reason: body.reason, leftToPay };
}

export const fetchLeftToPay = async (agreementId: string): Promise<LeftToPay> =>
    readLeftToPay((await api.get(`/agreements/${agreementId}/left-to-pay`)).data.data);

const amountField = (input: PaymentLinkInput): Pick<PaymentLinkPayload, 'amount_gross' | 'pay_off'> =>
    input.payOff
        ? { pay_off: true }
        : { amount_gross: normalizeAmountString(input.amountGross) ?? input.amountGross };

export const toPaymentLinkPayload = (input: PaymentLinkInput): PaymentLinkPayload => ({
    ...amountField(input),
    send_via_email: input.sendViaEmail,
    send_via_sms: input.sendViaSms,
    ...(input.replaceLive ? { replace_live: true as const } : {}),
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const readChannels = (raw: unknown): PaymentLinkSummary['channels'] | null => {
    if (!isRecord(raw) || typeof raw.email !== 'boolean' || typeof raw.sms !== 'boolean') return null;
    return raw.email || raw.sms ? { email: raw.email, sms: raw.sms } : null;
};

/** Null when the body lacks a usable amount or channels; the link URL is never read. */
export function readPaymentLinkSummary(raw: unknown): PaymentLinkSummary | null {
    if (!isRecord(raw) || typeof raw.amount !== 'string') return null;
    const channels = readChannels(raw.channels);
    if (channels === null) return null;

    return {
        amount: raw.amount,
        paysOff: raw.pays_off === true,
        channels,
        createdAt: typeof raw.created_at === 'string' ? raw.created_at : '',
        expiresAt: typeof raw.expires_at === 'string' ? raw.expires_at : '',
    };
}

export const fetchLivePaymentLink = async (agreementId: string): Promise<PaymentLinkSummary | null> =>
    readPaymentLinkSummary((await api.get(`/agreements/${agreementId}/payment-links/live`)).data?.data);

/** A 201 means the link went out, so an unreadable body falls back to what was submitted. */
export async function sendPaymentLink(agreementId: string, input: PaymentLinkInput): Promise<SentPaymentLink> {
    const payload = toPaymentLinkPayload(input);
    const response = await api.post(`/agreements/${agreementId}/payment-links`, payload);
    const raw: unknown = response.data?.data;
    const summary = readPaymentLinkSummary(raw) ?? {
        amount: input.payOff ? input.leftToPay : (payload.amount_gross ?? ''),
        paysOff: input.payOff === true,
        channels: { email: payload.send_via_email, sms: payload.send_via_sms },
        createdAt: '',
        expiresAt: '',
    };

    return { ...summary, id: isRecord(raw) ? String(raw.id ?? '') : '' };
}
